// @flow
import FormClientFooter from 'components/FormCustomer/FormClientFooter'
import { ResourceFormContext } from 'components/ResourceFill/providers/ResourceFormProvider'
import { ResourceNavigationContext } from 'components/ResourceFill/providers/ResourceNavigationProvider'
import { ResourceResponseContext } from 'components/ResourceFill/providers/ResourceResponseProvider'
import { ResourceSaveContext } from 'components/ResourceFill/providers/ResourceSaveProvider'
import { ResourceSessionsContext } from 'components/ResourceFill/providers/ResourceSessionsProvider'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import { ResourceStripeContext } from 'components/ResourceFill/providers/ResourceStripeProvider'
import { ResourceSubmitContext } from 'components/ResourceFill/providers/ResourceSubmitProvider'
import useQuery from 'hooks/useQuery'
import { useUpdateResponseQueue } from 'queries/response_queue/useUpdateResponseQueue'
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import useSearchParams from '@/hooks/useSearchParams'

type Props = {
  setSaveEditLaterModalOpen: (boolean) => void,
}

function ResourceFillFooter({ setSaveEditLaterModalOpen }: Props): React.Node {
  const query = useQuery()
  const history = useHistory()
  const searchParams = useSearchParams()

  const { mode, isTablet, formIndex } =
    React.useContext(ResourceSharedContext) || {}

  const { sessions, sessionId } =
    React.useContext(ResourceSessionsContext) || {}

  const { submit, isWaiting, submitting } =
    React.useContext(ResourceSubmitContext) || {}

  const { saving } = React.useContext(ResourceSaveContext) || {}

  const { allDirty } = React.useContext(ResourceFormContext) || {}

  const { response, isFetching } =
    React.useContext(ResourceResponseContext) || {}

  const { onPrevForm, onNextForm } =
    React.useContext(ResourceNavigationContext) || {}

  const { stripeError } = React.useContext(ResourceStripeContext) || {}

  const { updateResponseQueue, isLoading: isUpdateResponseQueueLoading } =
    useUpdateResponseQueue()

  const formsCount = React.useMemo(() => {
    if (!sessionId) return 0
    if (!sessions) return 0

    const currentSession = sessions[sessionId]

    return currentSession?.responses?.length || 0
  }, [sessionId, sessions])

  const addResponsesToQueue = React.useCallback(async () => {
    if (!sessionId) return
    if (!sessions) return

    const currentSession = sessions[sessionId]

    if (!currentSession) return

    const responses = currentSession.responses
      .filter((response) => {
        if (!response?.form?.has_office_use_questions) return false
        return true
      })
      .map((response) => {
        return {
          response_uid: !response.packet_response
            ? response.id
            : typeof response.packet_response === 'string'
              ? response.packet_response
              : response.packet_response.id,
          is_packet: !!response.packet_response,
          is_queued: true,
        }
      })

    // Remove duplicates
    const uniqueResponses = []
    responses.forEach((response) => {
      if (
        !uniqueResponses?.find((r) => r.response_uid === response.response_uid)
      ) {
        uniqueResponses.push(response)
      }
    })

    await Promise.all(
      uniqueResponses.map((response) => updateResponseQueue(response))
    )

    if (isTablet) {
      history.push('/t')
    } else {
      history.push('/')
    }

    toast.success('Added to queue')
  }, [history, isTablet, sessionId, sessions, updateResponseQueue])

  const hasExtraSubmitButton = React.useMemo(() => {
    if (!submit) return

    if (
      mode === 'office' &&
      (isTablet || response?.message?.sent_at === null)
    ) {
      return {
        label: 'Queue',
        disabled: isUpdateResponseQueueLoading,
        loading: isUpdateResponseQueueLoading,
        onClick: async () => {
          const saved = await submit({ willEnqueue: true })
          if (saved) {
            await addResponsesToQueue()
          }
        },
      }
    }

    return null
  }, [
    isTablet,
    addResponsesToQueue,
    isUpdateResponseQueueLoading,
    mode,
    response?.message?.sent_at,
    submit,
  ])

  const getOfficeSubmitLabel = React.useCallback(() => {
    if (isTablet) {
      return 'Start client'
    }

    if (isWaiting) {
      return 'Send'
    }

    return 'Save'
  }, [isTablet, isWaiting])

  const getSubmitLabel = React.useCallback(() => {
    if (mode === 'office') {
      return getOfficeSubmitLabel()
    }

    if (mode === 'internal') {
      return 'Save'
    }

    return 'Submit'
  }, [getOfficeSubmitLabel, mode])

  if (mode === 'preview') return null

  const isInternalForm =
    history?.location?.pathname?.includes('/internal-form/')
  const isAnonymousForm =
    response?.message?.message_type === 'anonymous_public_response'
  const isFillResponse = history?.location?.pathname?.includes('/r/')

  const responseTypeParams = searchParams.get('response_type')
  const isPacket = responseTypeParams === 'packet'

  const packetOfficeUse = history.location.pathname.includes(
    'fillPacketOfficeUseQuestions'
  )

  if (!submit) return null

  return (
    <FormClientFooter
      buttonsDisabled={isFetching}
      currentStep={formIndex}
      disabled={allDirty}
      extraSubmitButton={hasExtraSubmitButton}
      isAnonymousForm={isAnonymousForm}
      isInternal={isInternalForm}
      isKiosk={query.get('isKiosk') === 'true'}
      isOfficeUse={mode === 'office'}
      isTablet={isTablet}
      loading={submitting}
      loadingNext={saving}
      loadingPrev={saving}
      notFixedBottom={
        isInternalForm || (isFillResponse && !isPacket && !packetOfficeUse)
      }
      onBack={onPrevForm}
      onNext={onNextForm}
      onSaveEditLater={() => setSaveEditLaterModalOpen(true)}
      onSubmit={({ willEnqueue = false, lockInternalForm = false }) =>
        submit({ willEnqueue, lockInternalForm })
      }
      stripeError={stripeError}
      submitLabel={getSubmitLabel()}
      totalSteps={formsCount}
    />
  )
}

export default ResourceFillFooter
